import { useEffect, useRef, memo } from "react";
import * as echarts from "echarts";
import type { TimeSeriesChartProps, TooltipFormatterParams } from "./types";

const defaultColorPalette = [
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#ec4899",
  "#6366f1",
];

function TimeSeriesChart({
  series = [],
  title = "Time Series Chart",
  xAxisLabel = "Date",
  yAxes = [{ name: "Value", position: "left" }],
  height = "400px",
  theme = "light",
  customTooltip,
  tooltipFormatter,
  samplingThreshold = 1000,
  enableAnimation = true,
  animationDuration = 1000,
  showLegend = true,
  legendPosition = "top",
  showDataZoom = true,
  colorPalette = defaultColorPalette,
}: TimeSeriesChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    const initChart = (): void => {
      if (!chartRef.current) return;
      if (yAxes.length === 0) return;

      // Dispose existing chart
      if (echartsRef.current) {
        echartsRef.current.dispose();
      }

      // Initialize chart
      echartsRef.current = echarts.init(chartRef.current, theme);

      // Default tooltip formatter
      const defaultTooltipFormatter = (
        params: TooltipFormatterParams,
      ): string => {
        if (!params || params.length === 0) return "";

        const date = new Date(params[0].value[0]);
        let content = `<div style="font-weight: bold; margin-bottom: 8px;">${date.toLocaleDateString()}</div>`;

        params.forEach((param) => {
          const value = param.value[1];
          content += `
            <div style="margin-bottom: 4px; display: flex; align-items: center;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${
                param.color
              }; border-radius: 50%; margin-right: 8px;"></span>
              <span style="font-weight: 500;">${param.seriesName}:</span>
              <span style="margin-left: 8px;">${
                typeof value === "number"
                  ? value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : value
              }</span>
            </div>
          `;
        });

        return content;
      };

      // Process series data
      const processedSeries = series.map((s, index) => {
        const processedData: [number, number][] = s.data.map(
          (item): [number, number] => [
            typeof item[0] === "string"
              ? new Date(item[0]).getTime()
              : item[0] instanceof Date
                ? item[0].getTime()
                : Number(item[0]),
            Number(item[1]),
          ],
        );

        const seriesColor =
          s.color || colorPalette[index % colorPalette.length];

        return {
          name: s.name,
          type: s.type || "line",
          smooth: s.smooth ?? true,
          symbol: s.showSymbols ? "circle" : "none",
          symbolSize: s.symbolSize || 4,
          yAxisIndex: s.yAxisIndex || 0,
          sampling:
            (s.enableSampling ?? true) &&
            processedData.length > samplingThreshold
              ? "lttb"
              : undefined,
          large: processedData.length > 5000,
          largeThreshold: 5000,
          itemStyle: {
            color: seriesColor,
          },
          lineStyle: {
            width: s.lineWidth || 2,
            color: seriesColor,
          },
          areaStyle: s.showArea
            ? {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  {
                    offset: 0,
                    color: s.areaColor || `${seriesColor}33`,
                  },
                  {
                    offset: 1,
                    color: s.areaColor
                      ? s.areaColor.replace(/[\d.]+\)$/g, "0.01)")
                      : `${seriesColor}01`,
                  },
                ]),
              }
            : undefined,
          emphasis: {
            focus: "series" as const,
            lineStyle: {
              width: (s.lineWidth || 2) + 1,
            },
          },
          data: processedData,
        };
      });

      // Configure Y-axes
      const yAxisConfigs = yAxes.map((yAxis, index) => ({
        type: "value" as const,
        name: yAxis.name || "Value",
        position: yAxis.position || (index === 0 ? "left" : "right"),
        min: yAxis.min,
        max: yAxis.max,
        splitNumber: yAxis.splitNumber,
        axisLine: {
          show: true,
          lineStyle: {
            color: yAxis.axisLineColor || (theme === "dark" ? "#666" : "#999"),
          },
        },
        axisLabel: {
          color: yAxis.labelColor || (theme === "dark" ? "#ccc" : "#666"),
          formatter:
            yAxis.formatter ||
            ((value: number): string => value.toLocaleString()),
        },
        splitLine: {
          show: index === 0, // Only show grid lines for primary axis
          lineStyle: {
            color: theme === "dark" ? "#333" : "#f0f0f0",
          },
        },
        nameTextStyle: {
          color: yAxis.labelColor || (theme === "dark" ? "#ccc" : "#666"),
        },
      }));

      const option = {
        tooltip: customTooltip || {
          trigger: "axis" as const,
          axisPointer: {
            type: "cross" as const,
            label: {
              backgroundColor: "#6a7985",
            },
          },
          formatter: tooltipFormatter || defaultTooltipFormatter,
          backgroundColor:
            theme === "dark"
              ? "rgba(0, 0, 0, 0.8)"
              : "rgba(255, 255, 255, 0.95)",
          borderColor: theme === "dark" ? "#555" : "#ccc",
          borderWidth: 1,
          textStyle: {
            color: theme === "dark" ? "#fff" : "#333",
          },
          extraCssText:
            "box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 4px;",
        },
        legend: showLegend
          ? {
              data: series.map((s) => s.name),
              [legendPosition]:
                legendPosition === "top" || legendPosition === "bottom"
                  ? 10
                  : 20,
              orient:
                legendPosition === "left" || legendPosition === "right"
                  ? "vertical"
                  : "horizontal",
              textStyle: {
                color: theme === "dark" ? "#ccc" : "#666",
              },
            }
          : undefined,
        grid: {
          left: yAxes.find((y) => y.position === "left") ? "8%" : "3%",
          right: yAxes.find((y) => y.position === "right") ? "8%" : "3%",
          bottom: showDataZoom ? "15%" : "10%",
          top: showLegend
            ? legendPosition === "top"
              ? "20%"
              : "10%"
            : title
              ? "15%"
              : "5%",
          containLabel: false,
        },
        xAxis: {
          type: "time" as const,
          name: xAxisLabel,
          nameLocation: "middle" as const,
          nameGap: 30,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: theme === "dark" ? "#666" : "#999",
            },
          },
          axisLabel: {
            color: theme === "dark" ? "#ccc" : "#666",
            formatter: (value: number): string => {
              const date = new Date(value);
              return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
            },
          },
          nameTextStyle: {
            color: theme === "dark" ? "#ccc" : "#666",
          },
        },
        yAxis: yAxisConfigs,
        dataZoom: showDataZoom
          ? [
              {
                type: "inside" as const,
                start: 0,
                end: 100,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
              },
              {
                type: "slider" as const,
                start: 0,
                end: 100,
                height: 30,
                bottom: 10,
                handleStyle: {
                  color: theme === "dark" ? "#666" : "#ddd",
                },
                textStyle: {
                  color: theme === "dark" ? "#ccc" : "#666",
                },
              },
            ]
          : [
              {
                type: "inside" as const,
                start: 0,
                end: 100,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                moveOnMouseWheel: true,
              },
            ],
        series: processedSeries,
        animation:
          enableAnimation && series.every((s) => s.data.length < 10000),
        animationDuration: animationDuration,
        animationEasing: "cubicInOut" as const,
        backgroundColor: "transparent",
        color: colorPalette,
      };

      echartsRef.current.setOption(option, true);
    };

    initChart();

    // Setup ResizeObserver for responsive behavior
    if (chartRef.current && window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (echartsRef.current) {
          echartsRef.current.resize();
        }
      });
      resizeObserverRef.current.observe(chartRef.current);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (echartsRef.current) {
        echartsRef.current.dispose();
      }
    };
  }, [
    series,
    title,
    xAxisLabel,
    yAxes,
    theme,
    customTooltip,
    tooltipFormatter,
    samplingThreshold,
    enableAnimation,
    animationDuration,
    showLegend,
    legendPosition,
    showDataZoom,
    colorPalette,
  ]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: height,
        minHeight: "300px",
      }}
    />
  );
}

export default memo(TimeSeriesChart);
